import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { postNamedQuery } from '../../../../service'
import { useParameterReplacementEx } from '../../../../hooks/useAbundanceEngineParameters'
import { useVariables } from '../../../../providers/VariableProvider'

const PAGE_SIZE = 1000
export const MAX_ROWS = 10000
const EMPTY_ARRAY = []

/**
 * Fetches all pages of blueprint data sequentially until a short page is
 * received or MAX_ROWS is reached.  Designed for presentations that need the
 * complete dataset client-side (e.g. hierarchical table tree building).
 */
export default function useExhaustiveBlueprintData (blueprintCode, purposeCode, options = {}) {
  const { parameters = {}, scopePredicates } = options

  const { variables } = useVariables()
  const [resolvedParams] = useParameterReplacementEx(parameters, variables)

  const baseRequest = useMemo(() => {
    if (!blueprintCode || !purposeCode) return null
    const req = {
      code: blueprintCode,
      purposeCode,
      parameters: resolvedParams
    }
    if (scopePredicates?.length) req.scopePredicates = scopePredicates
    return req
  }, [blueprintCode, purposeCode, resolvedParams, scopePredicates])

  const requestKey = JSON.stringify(baseRequest)

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['exhaustive-blueprint-data', blueprintCode, purposeCode, requestKey],
    queryFn: async () => {
      let allRows = []
      let page = 0
      let lastMetadata = null

      while (allRows.length < MAX_ROWS) {
        const { data: result } = await postNamedQuery('coreData', 'execute-query-blueprint', {
          ...baseRequest,
          page,
          pageSize: PAGE_SIZE
        })
        const pageData = result?.data ?? []
        lastMetadata = result?.metadata ?? lastMetadata
        allRows = allRows.concat(pageData)

        if (pageData.length < PAGE_SIZE) break
        page++
      }

      const truncated = allRows.length >= MAX_ROWS
      if (truncated) allRows = allRows.slice(0, MAX_ROWS)

      return { rows: allRows, metadata: lastMetadata, truncated }
    },
    enabled: !!baseRequest,
    keepPreviousData: true
  })

  return {
    data: data?.rows ?? EMPTY_ARRAY,
    metadata: data?.metadata ?? null,
    isLoading,
    isFetching,
    error,
    truncated: data?.truncated ?? false
  }
}
